import { useEffect } from 'react';

const STYLE_ID = 'vantage-cta-sheen-v4';
const FONT_ATTR = 'data-vibe-font';

const CTA_SHEEN_CSS = `
/* Filled primary CTA — light streak sweep on hover (matches header nav-cta-btn pattern).
   Override --vantage-cta-sheen-accent / --vantage-cta-sheen-on-accent on a parent
   when luminance-picking a fallback accent (e.g. interior card CTAs). */
.vantage-cta-sheen {
  --_cta-sheen-accent: var(--vantage-cta-sheen-accent, var(--vibe-color-primary));
  --_cta-sheen-on-accent: var(--vantage-cta-sheen-on-accent, var(--vibe-color-text-on-primary));
  position: relative;
  overflow: hidden;
  background: var(--_cta-sheen-accent);
  border: 1.5px solid var(--_cta-sheen-accent);
  border-radius: 2px;
  color: var(--_cta-sheen-on-accent);
  text-decoration: none;
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease,
              transform 0.3s ease, box-shadow 0.3s ease;
}
.vantage-cta-sheen::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  transform: translateX(-120%);
  background: linear-gradient(
    105deg,
    transparent 0%,
    transparent 35%,
    rgba(255, 255, 255, 0.22) 45%,
    rgba(255, 255, 255, 0.42) 50%,
    rgba(255, 255, 255, 0.22) 55%,
    transparent 65%,
    transparent 100%
  );
  transition: transform 0.6s ease;
  pointer-events: none;
}
.vantage-cta-sheen:hover {
  background-color: color-mix(in srgb, var(--_cta-sheen-accent), black 14%);
  border-color: color-mix(in srgb, var(--_cta-sheen-accent), black 14%);
  color: var(--_cta-sheen-on-accent);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--_cta-sheen-accent) 35%, transparent);
}
.vantage-cta-sheen:hover::before {
  transform: translateX(120%);
}
.vantage-cta-sheen:active {
  transform: translateY(0);
  box-shadow: none;
}

@media (prefers-reduced-motion: reduce) {
  .vantage-cta-sheen::before {
    display: none;
  }
  .vantage-cta-sheen:hover {
    transform: none;
  }
}

/* Secondary fill + Hero CTA typography — use with \`vantage-cta-sheen\`. */
.vantage-cta-hero {
  --vantage-cta-sheen-accent: var(--vibe-color-secondary);
  --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "Raleway", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: uppercase;
  min-height: 51px;
  min-width: 25px;
  padding: 0 1.5rem;
}

/* Multihub form CTAs (read-only blocks) — match Hero cta_link.
   !important beats the lead-form's inline button styles.
   Tour-scheduler Continue/Submit is button.ml-auto.bg-primary (not calendar/time
   cells that also use bg-primary). */
.cf-form-wrap .mh-lead-form-submit-button,
.cf-form-wrap .mh-lead-form-success-cta,
.stf-form-wrap a.inline-block.bg-primary,
.stf-form-wrap button.ml-auto.bg-primary {
  --vantage-cta-sheen-accent: var(--vibe-color-secondary);
  --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-secondary);
  --_cta-sheen-accent: var(--vantage-cta-sheen-accent);
  --_cta-sheen-on-accent: var(--vantage-cta-sheen-on-accent);
  position: relative !important;
  overflow: hidden !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
  min-height: 51px !important;
  min-width: 25px !important;
  padding: 0 1.5rem !important;
  margin: 0;
  background: var(--_cta-sheen-accent) !important;
  border: 1.5px solid var(--_cta-sheen-accent) !important;
  border-radius: 2px !important;
  color: var(--_cta-sheen-on-accent) !important;
  font-family: "Raleway", sans-serif !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  letter-spacing: 0 !important;
  text-transform: uppercase !important;
  text-decoration: none !important;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease,
              transform 0.3s ease, box-shadow 0.3s ease;
}
.stf-form-wrap a.inline-block.bg-primary,
.stf-form-wrap button.ml-auto.bg-primary {
  width: auto !important;
  max-width: 258px !important;
  margin-left: auto !important;
}
.cf-form-wrap .mh-lead-form-submit-button::before,
.cf-form-wrap .mh-lead-form-success-cta::before,
.stf-form-wrap a.inline-block.bg-primary::before,
.stf-form-wrap button.ml-auto.bg-primary::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  transform: translateX(-120%);
  background: linear-gradient(
    105deg,
    transparent 0%,
    transparent 35%,
    rgba(255, 255, 255, 0.22) 45%,
    rgba(255, 255, 255, 0.42) 50%,
    rgba(255, 255, 255, 0.22) 55%,
    transparent 65%,
    transparent 100%
  );
  transition: transform 0.6s ease;
  pointer-events: none;
}
.cf-form-wrap .mh-lead-form-submit-button:hover,
.cf-form-wrap .mh-lead-form-success-cta:hover,
.stf-form-wrap a.inline-block.bg-primary:hover,
.stf-form-wrap button.ml-auto.bg-primary:hover {
  background-color: color-mix(in srgb, var(--_cta-sheen-accent), black 14%) !important;
  border-color: color-mix(in srgb, var(--_cta-sheen-accent), black 14%) !important;
  color: var(--_cta-sheen-on-accent) !important;
  transform: translateY(-1px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--_cta-sheen-accent) 35%, transparent);
}
.cf-form-wrap .mh-lead-form-submit-button:hover::before,
.cf-form-wrap .mh-lead-form-success-cta:hover::before,
.stf-form-wrap a.inline-block.bg-primary:hover::before,
.stf-form-wrap button.ml-auto.bg-primary:hover::before {
  transform: translateX(120%);
}
.cf-form-wrap .mh-lead-form-submit-button:active,
.cf-form-wrap .mh-lead-form-success-cta:active,
.stf-form-wrap a.inline-block.bg-primary:active,
.stf-form-wrap button.ml-auto.bg-primary:active {
  transform: translateY(0);
  box-shadow: none;
}
.cf-form-wrap .mh-lead-form-submit-button > *,
.cf-form-wrap .mh-lead-form-success-cta > * {
  position: relative;
  z-index: 10;
}
@media (prefers-reduced-motion: reduce) {
  .cf-form-wrap .mh-lead-form-submit-button::before,
  .cf-form-wrap .mh-lead-form-success-cta::before,
  .stf-form-wrap a.inline-block.bg-primary::before,
  .stf-form-wrap button.ml-auto.bg-primary::before {
    display: none;
  }
  .cf-form-wrap .mh-lead-form-submit-button:hover,
  .cf-form-wrap .mh-lead-form-success-cta:hover,
  .stf-form-wrap a.inline-block.bg-primary:hover,
  .stf-form-wrap button.ml-auto.bg-primary:hover {
    transform: none;
  }
}
`;

function ensureRalewayFont(): void {
  if (document.querySelector(`link[${FONT_ATTR}="raleway"]`)) return;
  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  document.head.appendChild(preconnect1);
  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect2);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
  link.setAttribute(FONT_ATTR, 'raleway');
  document.head.appendChild(link);
}

/**
 * Injects the filled CTA sheen-hover CSS into <head> exactly once.
 * Pair with `vantage-cta-sheen` on the anchor and a `relative z-10` label span.
 * Add `vantage-cta-hero` for secondary color + Hero CTA typography.
 * Also restyles Multihub lead-form / tour-scheduler CTAs inside `.cf-form-wrap`
 * / `.stf-form-wrap` to match the Hero CTA.
 */
export function useCtaSheenStyles(): void {
  useEffect(() => {
    ensureRalewayFont();
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CTA_SHEEN_CSS;
    document.head.appendChild(style);
  }, []);
}
